import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { DialogService, DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { MessageService, ConfirmationService, PrimeNGConfig } from 'primeng/api';
import { Title } from '@angular/platform-browser';
import { NavigationService } from '../../../services/navigation/navigation.service';
import { CommonService } from '../../../services/common/common.service';
import { ToolsService } from '../../../services/tools/tools.service';
import { Column } from '../../interfaces/column.interface';
import { FitnessSessionsDataService, FitnessSessionsColumnsService } from '../../services';
import { DialogFitnessSessionFormComponent } from './fitness-session-form/fitness-session-form.component';
import { DialogFitnessSessionDetailsComponent } from './fitness-session-details/fitness-session-details.component';
import { FitnessSession, FitnessSessionsResult } from '../../interfaces';
import { FileUpload } from 'primeng/fileupload';

@Component({
  selector: 'app-fitness-sessions',
  templateUrl: './fitness-sessions.component.html',
  styleUrls: ['./fitness-sessions.component.scss'],
  providers: [MessageService, ConfirmationService, DialogService],
})
export class FitnessSessionsComponent implements OnInit, OnDestroy {

  private fitnessSessionAddDialog: DynamicDialogRef = new DynamicDialogRef();
  private fitnessSessionEditDialog: DynamicDialogRef = new DynamicDialogRef();
  private fitnessSessionDetailsDialog: DynamicDialogRef = new DynamicDialogRef();
  public fitnessSessionToDelete: FitnessSession = {};
  public fitnessSessions: FitnessSession[] = [];
  public fitnessSession: FitnessSession = {};
  public selectedFitnessSessions: FitnessSession[] = [];
  public submitted: boolean = false;
  public parameters: {[param: string]: string | string[]} = {offset: '0', limit: '100'};
  public chunkSize: number = 200;
  public columns: Column[] = [];
  public isLoading: boolean = false;
  public isInitialized: boolean = false;
  public locale: string = (localStorage.language)?localStorage.language:'en';
  private _selectedColumns: Column[] = [];
  @ViewChild('importer', { static: false })
  public importer?: FileUpload;
  public importChooseLabel: string = 'Import';
  public importLabel: string = 'Import CSV';

  constructor(
    private translate: TranslateService,
    private navigation: NavigationService,
    private common: CommonService,
    private router: Router,
    private dialogService: DialogService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private primengConfig: PrimeNGConfig,
    private titleService: Title,
    private fitnessSessionsDataService: FitnessSessionsDataService,
    private fitnessSessionsColumnsService: FitnessSessionsColumnsService,
  ) {
    this.importChooseLabel = this.translate.instant('IMPORT');
    this.importLabel = this.translate.instant('IMPORTCSV');
  }

  ngOnInit(): void {
    console.log('FitnessSessionsComponent', 'ngOnInit');
    this.isInitialized = true;
    this.titleService.setTitle('Fitness Sessions - Management');
    this.primengConfig.ripple = true;
    this.getFitnessSessions();
  }

  ngOnDestroy(): void {
    console.log('FitnessSessionsComponent', 'ngOnDestroy');
    this.isInitialized = false;
  }

  getFitnessSessions(): void {
    if (!this.isInitialized) {
      return;
    }
    if (parseInt(this.parameters.offset + '', 10) === 0) {
      this.isLoading = true;
    }
    this.fitnessSessionsDataService.getFitnessSessions(this.parameters).subscribe((response: FitnessSessionsResult|any) => {
      this.isLoading = false;
      if (response !== null) {
        this.setColumns();
        if (parseInt(this.parameters.offset + '', 10) === 0) {
          this.fitnessSessions = response.results;
        } else {
          this.fitnessSessions = this.fitnessSessions.concat(response.results);
        }
        if (response.inlineCount > this.fitnessSessions.length) {
          this.parameters.offset = (parseInt(this.parameters.offset + '', 10) + parseInt(this.parameters.limit + '', 10)) + '';
          this.getFitnessSessions();
        } else {
          this.parameters.offset = '0';
        }
      }
    }, (error: any) => {;
      this.isLoading = false;
      this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('FITNESSSESSIONSNOTLOADED')});
    });
  }

  importFitnessSessions(event: any): void {
    if (typeof event.currentFiles === 'undefined') {
      return;
    }
    if (event.currentFiles.length === 0) {
      return;
    }
    const inputFile = event.currentFiles[0];
    const reader = new FileReader();
    let rows: any[] = [];
    reader.onload = (e: any) => {
      if (typeof this.importer !== 'undefined') {
        this.importer.clear();
      }
      const text = <string>e.target.result;
      rows = ToolsService.csvToArray(this.columns, text, ',');
      if (rows.length > 0) {
        let proceed = 0;
        const j = rows.length;
        for (let i = 0; i < j; i += this.chunkSize) {
          this.isLoading = true;
          const chunk = rows.slice(i, i + this.chunkSize);
          if (typeof chunk[0].id === 'undefined') {
            this.fitnessSessionsDataService.addFitnessSessions(chunk).subscribe((response: any) => {
              this.isLoading = false;
              if (typeof response.length !== 'undefined') {
                proceed += response.length;
                this.messageService.add({severity: 'success', summary: this.translate.instant('NOTIFICATION'), detail: proceed + ' ' + this.translate.instant('FITNESSSESSIONSCREATED')});
                if (proceed === j) {
                  this.getFitnessSessions();
                }
              } else {
                this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('FITNESSSESSIONSNOTCREATED')});
              }
            }, (error: any) => {
              this.isLoading = false;
              console.warn('import error', error);
              let msg = '';
              if (typeof error.error.length !== 'undefined') {
                msg = error.error;
              } else if (typeof error.error.errors.errors.children !== 'undefined') {
                const children = error.error.errors.errors.children;
                for (const field in children) {
                  if (typeof children[field].errors !== 'undefined') {
                    msg += field + ' ' + children[field].errors.join(', ');
                  }
                }
              }
              this.messageService.add({severity: 'warn', summary: this.translate.instant('ERROR'), detail: this.translate.instant('FITNESSSESSIONSNOTCREATED') + ' ' + msg});
            });
          } else {
            this.fitnessSessionsDataService.updateFitnessSessions(chunk).subscribe((response: any) => {
              this.isLoading = false;
              if (typeof response.length !== 'undefined') {
                proceed += response.length;
                this.messageService.add({severity: 'success', summary: this.translate.instant('NOTIFICATION'), detail: proceed + ' ' + this.translate.instant('FITNESSSESSIONSUPDATED')});
                if (proceed === j) {
                  this.getFitnessSessions();
                }
              } else {
                this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('FITNESSSESSIONSNOTUPDATED')});
              }
            }, (error: any) => {
              this.isLoading = false;
              console.warn('import error', error);
              let msg = '';
              if (typeof error.error.length !== 'undefined') {
                msg = error.error;
              } else if (typeof error.error.errors.errors.children !== 'undefined') {
                const children = error.error.errors.errors.children;
                for (const field in children) {
                  if (typeof children[field].errors !== 'undefined') {
                    msg += field + ' ' + children[field].errors.join(', ');
                  }
                }
              }
              this.messageService.add({severity: 'warn', summary: this.translate.instant('ERROR'), detail: this.translate.instant('FITNESSSESSIONSNOTCREATED') + ' ' + msg});
            });
          }
        }
      }
    };
    reader.readAsText(inputFile);
  }

  setColumns() {
    if (this.columns.length > 0) {
      return;
    }
    this.columns = this.fitnessSessionsColumnsService.getColumns();
    for (let i = 0; i < this.columns.length; i++) {
      if (this.columns[i].show) {
        this._selectedColumns.push(this.columns[i]);
      }
    }
  }

  @Input()
  get selectedColumns(): any[] {
      return this._selectedColumns;
  }

  set selectedColumns(val: any[]) {
      // restore original order
      this._selectedColumns = this.columns.filter((col: any) => val.includes(col));
      this.updateColumns();
  }

  updateColumns() {
    for (let i = 0; i < this.columns.length; i++) {
      const show = this._selectedColumns.includes(this.columns[i]);
      this.columns[i].show = show;
      this.navigation.setParamValue('fitnessSessions.' + this.columns[i].field + '.showFiled', show);
    }
  }

  deleteSelectedFitnessSessions() {
    this.confirmationService.confirm({
      message: 'Are you sure you want to delete the selected Fitness Sessions?',
      header: 'Confirm',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.fitnessSessionsDataService.deleteFitnessSessions(this.selectedFitnessSessions).subscribe((response) => {
          if (response === null) {
            this.messageService.clear();
            this.messageService.add({severity:'success', summary: 'Successful', detail: 'Fitness Sessions Deleted', life: 3000});
            this.getFitnessSessions();
          } else {
            this.messageService.clear();
            this.messageService.add({severity: 'error', summary: 'Error', detail: 'Fitness Sessions not deleted'});
          }
        }, (error: any) => {
          this.messageService.clear();
          this.messageService.add({severity: 'error', summary: 'Error', detail: 'Fitness Sessions not deleted'});
        });
        this.selectedFitnessSessions = [];
      }
    });
  }

  exportPdf() {
    ToolsService.exportPdf(this.fitnessSessions, this.columns, 'FitnessSessions');
  }

  exportExcel() {
    ToolsService.exportExcel(this.fitnessSessions, this.columns, 'FitnessSessions');
  }

  exportCsv() {
    ToolsService.exportCsv(this.fitnessSessions, this.columns, 'FitnessSessions');
  }

  refresh() {
    this.fitnessSessionsDataService.clearCache();
    this.getFitnessSessions();
  }

  fitnessSessionAdd(): void {
    if (!this.navigation.getParamValue('fitnessSessions.addInDialog', true)) {
      this.router.navigate(['/fitness-sessions/add']);
      return;
    }
    this.fitnessSessionAddDialog = this.dialogService.open(DialogFitnessSessionFormComponent, {
      header: '', // this.translate.instant('NEWFITNESSSESSION'),
      width: '80%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        mode: 'add',
        fitnessSession: {id: null},
        fitnessClasses: this.fitnessSessionsColumnsService.fitnessClasses,
        coaches: this.fitnessSessionsColumnsService.coaches,
        users: this.fitnessSessionsColumnsService.users,
      },
    });

    this.fitnessSessionAddDialog.onClose.subscribe((response: any) => {
      if (response && response.refresh) {
        this.messageService.clear();
        this.messageService.add({severity: 'info', summary: 'Success', detail: 'FitnessSession added'});
        this.getFitnessSessions();
      }
    });
  }

  fitnessSessionEdit(fitnessSession: FitnessSession): void {
    if (!this.navigation.getParamValue('fitnessSessions.editInDialog', true)) {
      this.router.navigate(['/fitness-sessions/edit/' + fitnessSession.id]);
      return;
    }
    this.fitnessSessionEditDialog = this.dialogService.open(DialogFitnessSessionFormComponent, {
      header: '', // this.translate.instant('EDITFITNESSSESSION') + ' ' + fitnessSession.name,
      width: '70%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        mode: 'edit',
        fitnessSession: fitnessSession,
        fitnessClasses: this.fitnessSessionsColumnsService.fitnessClasses,
        coaches: this.fitnessSessionsColumnsService.coaches,
        users: this.fitnessSessionsColumnsService.users,
      },
    });

    this.fitnessSessionEditDialog.onClose.subscribe((response: any) => {
      if (response && response.refresh) {
        this.messageService.clear();
        this.messageService.add({severity: 'success', summary: 'Success', detail: 'FitnessSession updated'});
        this.getFitnessSessions();
      }
    });
  }

  fitnessSessionDetails(fitnessSession: FitnessSession): void {
    if (!this.navigation.getParamValue('fitnessSessions.detailsInDialog', true)) {
      this.router.navigate(['/fitness-sessions/details/' + fitnessSession.id]);
      return;
    }
    this.fitnessSessionDetailsDialog = this.dialogService.open(DialogFitnessSessionDetailsComponent, {
      width: '70%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        fitnessSession: fitnessSession,
      }
    });
  }

  fitnessSessionDelete(fitnessSession: FitnessSession): void {
    this.fitnessSessionToDelete = fitnessSession;
    this.messageService.clear();
    this.messageService.add({
      key: 'fitnessSessionDelete',
      sticky: true,
      severity: 'warn',
      summary: 'Are you sure you want delete ' + fitnessSession.name + ' ?',
      detail: 'Confirm to proceed'
    });
  }

  onDeleteConfirm(): void {
    this.fitnessSessionsDataService.deleteFitnessSession(this.fitnessSessionToDelete).subscribe((res: any) => {
      this.messageService.clear();
      this.messageService.add({severity: 'success', summary: 'Success', detail: 'Fitness Session deleted'});
      this.getFitnessSessions();
    }, (error: any) => {
      this.messageService.clear();
      this.messageService.add({severity: 'error', summary: 'Error', detail: 'Fitness Session not deleted'});
    });
  }

  onDeleteReject(): void {
    this.fitnessSessionToDelete = {};
    this.messageService.clear('fitnessSessionDelete');
  }

}
